import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as DotsVerticalIcon } from "components/Icons/dots-vertical.svg";
import { SelectRow, UnselectRow } from "components/Table/useTableSelectable";
import { Tooltip } from "components/Tooltips/Tooltip";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useContextMenu } from "hooks/contextMenu";
import { HTMLElementType, useIsOverflown } from "hooks/useIsOverflown";
import { kebabCase } from "lodash";
import { useCallback, useEffect, useRef } from "react";
import { Column, OLTableActions, Row } from "./OLTable";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    root: {
      "& td": {
        whiteSpace: "nowrap",
        maxWidth: "400px",
      },
      "& td + td": {
        "[dir='rtl'] &": {
          borderLeft: "none",
          borderRight: `1px solid ${theme.gray100}`,
        },
      },
      "&:hover": {
        background: "#fafafa !important",
      },
      "& td > *": {
        verticalAlign: "middle",
      },
    },
    clickable: {
      cursor: "pointer",
    },
    cell: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cellText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    borderRight: {
      whiteSpace: "nowrap",
    },
    MoreIcon: {
      color: theme.gray400,
      cursor: "pointer",
      fontSize: 24,
      verticalAlign: "middle",
    },
    tableCellWithIcon: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(4),
    },
    actionCell: {
      textAlign: "center",
      whiteSpace: "nowrap",
      width: "1%",
      position: "sticky",
      right: 0,
      zIndex: 3,
      overflow: "hidden",
      background: "inherit",
      borderLeft: `1px solid ${theme.gray100} !important`,
    },
    fakeCell: {
      padding: "0 !important",
    },
    disabled: { cursor: "not-allowed" },
    fraudRow: { backgroundColor: `${theme.red50} !important` },
    icon: {
      width: theme.spacing(12),
      height: theme.spacing(12),
      color: theme.red500,
      display: "flex",
      alignItems: "center",
      opacity: 1,
      margin: 0,
    },
    selectedRow: {
      background: "#F2EFFF !important",
    },
    checkbox: {
      padding: 0,
      marginRight: theme.spacing(4),
    },
    selectWrapper: {
      display: "flex",
      alignItems: "center",
    },
    alignRight: {
      justifyContent: "flex-end",
    },
    alignCenter: {
      justifyContent: "center",
    },
  }),
);

type Props = {
  onRowClick?: (id: string) => void;
  actions?: Array<OLTableActions>;
  className?: string;
  id: string;
  items: Row;
  selectable?: {
    disabled: boolean;
    selected: boolean;
    selectRow: SelectRow;
    unselectRow: UnselectRow;
  };
  columns?: Column[];
  "data-test-id"?: string;
  disableActions?: boolean;
  columnRefs: React.MutableRefObject<(HTMLTableCellElement | null)[]>;
  calculateColumnLeftOffset: (index: number) => number;
};

export function OLTableRow({
  onRowClick,
  actions,
  className = "",
  id,
  items,
  selectable,
  columns,
  "data-test-id": dataTestId,
  disableActions,
  columnRefs,
  calculateColumnLeftOffset,
}: Props) {
  const classes = useStyles();
  const contextMenu = useContextMenu();
  const itemsRef = useRef<HTMLElementType[]>([]);
  const { observeElements, isOverflown } = useIsOverflown();

  const isAnyActionAvailable = actions?.some((action) => {
    if (action.hideCondition) {
      return !action.hideCondition(items);
    }
    return true;
  });

  const findColumnName = useCallback(
    (index: number) => {
      const column = columns?.[index];

      return column && column.label
        ? column.label.toLowerCase().replaceAll(" ", "-")
        : null;
    },
    [columns],
  );

  useEffect(() => {
    observeElements(itemsRef.current);
    // eslint-disable-next-line
  }, []);

  return (
    <TableRow
      data-test-id={dataTestId}
      className={`${classes.root} ${className} ${
        onRowClick ? classes.clickable : ""
      } ${items.metadata?.fraudProbability >= 0.5 ? classes.fraudRow : ""}`}
    >
      {items?.cells.map((item, index) => {
        const columnName = dataTestId && findColumnName(index);
        const isFirstColumn = index === 0;
        const isSticky = columns?.[index].isSticky;
        const isLastColumn = items.cells.length === index + 1;

        return (
          <TableCell
            data-test-id={dataTestId && `${dataTestId}-${columnName}`}
            className={`${classes.cell} ${classes.borderRight} ${
              item.tooltip ? classes.tableCellWithIcon : ""
            } ${selectable?.selected ? classes.selectedRow : ""}`}
            key={index}
            padding="none"
            onClick={() => {
              if (selectable) {
                return selectable.selected
                  ? selectable.unselectRow(id)
                  : !selectable.disabled &&
                      selectable.selectRow({ id, data: items.data });
              }

              item.onDirectClick && item.onDirectClick();
              !item.nonClickable && onRowClick?.(id);
            }}
            sx={{
              maxWidth: item.maxWidth || "unset",
              ...(isSticky && {
                position: "sticky",
                left: calculateColumnLeftOffset(index),
                zIndex: 3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                background: "inherit",
              }),
              ...(isLastColumn && {
                borderRight: "none !important",
              }),
            }}
            data-hj-suppress={item.dataHjSuppress}
          >
            <div
              className={`${classes.selectWrapper}
               ${item.align === "right" ? classes.alignRight : ""}
               ${item.align === "center" ? classes.alignCenter : ""}
               `}
            >
              {selectable && isFirstColumn && (
                <Checkbox
                  disabled={!selectable.selected && selectable.disabled}
                  checked={selectable.selected}
                  className={classes.checkbox}
                  onClick={() => {
                    selectable.selected
                      ? selectable.unselectRow(id)
                      : selectable.selectRow({ id, data: items.data });
                  }}
                />
              )}
              {item.tooltip && (
                <TooltipWithIcon
                  className={classes.icon}
                  icon={<AlertTriangleIcon />}
                  text={item.tooltip}
                  isWhite
                  placement="bottom-start"
                />
              )}
              <Tooltip
                text={
                  item.disableOverflowTooltip
                    ? ""
                    : item.customOverflowTooltip || item.content
                }
                disableHoverListener={
                  item.disableOverflowTooltip ||
                  (Array.isArray(isOverflown) && !isOverflown[index])
                }
                placement="bottom-start"
              >
                <span
                  className={classes.cellText}
                  ref={(el: HTMLElementType) => {
                    itemsRef.current[index] = el;
                  }}
                >
                  {item.content || typeof item.content === "number"
                    ? item.content
                    : item.emptyString || "-"}
                </span>
              </Tooltip>
            </div>
          </TableCell>
        );
      })}

      {actions ? (
        <TableCell className={classes.actionCell}>
          {isAnyActionAvailable && (
            <DotsVerticalIcon
              className={classes.MoreIcon}
              onClick={contextMenu.handleMenuOpen}
              data-test-id={dataTestId && dataTestId + `-actions`}
            />
          )}
          <Menu
            anchorEl={contextMenu.anchor}
            anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
            onClose={contextMenu.handleMenuClose}
            open={contextMenu.isMenuOpen}
            transformOrigin={{ horizontal: "center", vertical: "top" }}
          >
            {actions.map((action, index) => {
              return (
                !action.hideCondition?.(items) &&
                (action.disableCondition?.(items) ? (
                  <div className={classes.disabled}>
                    <MenuItem
                      disabled
                      key={index}
                      data-test-id={
                        dataTestId &&
                        dataTestId + `-actions-${kebabCase(action.label)}`
                      }
                    >
                      {action.label}
                    </MenuItem>
                  </div>
                ) : (
                  <MenuItem
                    key={index}
                    data-test-id={
                      dataTestId &&
                      dataTestId + `-actions-${kebabCase(action.label)}`
                    }
                    onClick={() => {
                      action.action?.(id, items);
                      contextMenu.handleMenuClose();
                    }}
                  >
                    {action.label}
                  </MenuItem>
                ))
              );
            })}
          </Menu>
        </TableCell>
      ) : disableActions ? null : (
        <TableCell className={classes.fakeCell}></TableCell>
      )}
    </TableRow>
  );
}
